import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight, Upload, Trash2, FileSpreadsheet, FileText, FileImage, Globe, PencilLine, ShieldCheck, PartyPopper } from "lucide-react";
import VendorVerification from "@/pages/VendorVerification";

const STEPS = [
  { n: 1, title: "Create Account" },
  { n: 2, title: "Business Info" },
  { n: 3, title: "Branding" },
  { n: 4, title: "Menus & Services" },
  { n: 5, title: "Verification" },
  { n: 6, title: "Finish" },
];

export default function OnboardingWizard() {
  const { user, register: doRegister, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [progress, setProgress] = useState({ data: {} });
  const [vendorId, setVendorId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadProgress = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/onboarding/progress");
      setProgress(data);
      setStep(data.current_step || 1);
      if (data.data?.vendor_id) setVendorId(data.data.vendor_id);
    } catch {}
  }, [user]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const saveProgress = async (newStep, patch) => {
    setSaving(true);
    try {
      const merged = { ...progress.data, ...(patch || {}) };
      const completed = Array.from(new Set([...(progress.completed_steps || []), step])).filter(n => n < newStep || n === step);
      const { data } = await api.put("/onboarding/progress", { current_step: newStep, completed_steps: completed, data: merged });
      setProgress(data);
      setStep(newStep);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const goto = (n) => { if (n >= 1 && n <= 6) setStep(n); };

  const pct = Math.round(((step - 1) / (STEPS.length - 1)) * 100);

  return (
    <div data-testid="onboarding-wizard" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <div className="eyebrow mb-3">Vendor Onboarding</div>
      <h1 className="text-4xl sm:text-5xl mb-2">Set up your NContro listing</h1>
      <p className="text-[color:var(--text-muted)] max-w-xl">
        Six quick steps. Save any time and finish later — your progress is stored automatically.
      </p>

      {/* Progress */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-2 text-xs">
          <span className="eyebrow">Step {step} of {STEPS.length}</span>
          <span className="text-[color:var(--text-muted)]" data-testid="progress-percent">{pct}% complete</span>
        </div>
        <div className="w-full h-2 rounded-full bg-[color:var(--surface)] overflow-hidden">
          <div data-testid="progress-bar" className="h-full bg-[color:var(--accent)] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {STEPS.map((s) => (
            <button key={s.n} onClick={() => goto(s.n)} data-testid={`step-nav-${s.n}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${step === s.n ? "bg-[color:var(--text)] text-white border-[color:var(--text)]" : "bg-white border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]"}`}>
              {s.n}. {s.title}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8">
        {step === 1 && <Step1 user={user} onDone={(d) => saveProgress(2, d)} />}
        {step === 2 && <Step2 data={progress.data} onBack={() => goto(1)} onDone={(vendor) => { setVendorId(vendor.vendor_id); saveProgress(3, { vendor_id: vendor.vendor_id, business: vendor }); }} />}
        {step === 3 && <Step3 vendorId={vendorId} onBack={() => goto(2)} onDone={() => saveProgress(4)} />}
        {step === 4 && <Step4 vendorId={vendorId} vendorCategory={progress?.data?.business?.category} onBack={() => goto(3)} onDone={() => saveProgress(5)} />}
        {step === 5 && <Step5 vendorId={vendorId} onBack={() => goto(4)} onDone={() => saveProgress(6)} />}
        {step === 6 && <Step6 onDashboard={() => nav("/vendor/dashboard")} onMore={() => goto(2)} />}
      </div>

      {saving && <div className="text-center text-xs text-[color:var(--text-muted)] mt-3">Saving…</div>}
    </div>
  );
}

/* ---------- STEP 1 ---------- */
function Step1({ user, onDone }) {
  const { register: doRegister } = useAuth();
  const [form, setForm] = useState({ email: user?.email || "", password: "", confirm: "" });
  const [tos, setTos] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (user) { onDone({}); return; } // already logged in
    if (!form.email || !form.password) return toast.error("Fill all fields");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    if (!tos || !privacy) return toast.error("Please agree to both documents");
    setLoading(true);
    try {
      await doRegister({ email: form.email, password: form.password, name: form.email.split("@")[0], role: "vendor" });
      toast.success("Account created");
      onDone({});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="step-1">
      <h2 className="text-2xl font-semibold mb-2">Create your account</h2>
      <p className="text-sm text-[color:var(--text-muted)] mb-6">Signed in as <strong className="text-[color:var(--text)]">{user?.email}</strong>. {user ? "Continue to next step." : "Enter your business email and a secure password."}</p>
      {!user && (
        <div className="space-y-4">
          <div><Label className="eyebrow">Business email</Label><Input data-testid="ob-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-2 h-11 bg-white" /></div>
          <div><Label className="eyebrow">Password</Label><Input data-testid="ob-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="mt-2 h-11 bg-white" /></div>
          <div><Label className="eyebrow">Confirm password</Label><Input data-testid="ob-confirm" type="password" value={form.confirm} onChange={e => setForm({...form, confirm: e.target.value})} className="mt-2 h-11 bg-white" /></div>
          <label className="flex items-start gap-3 cursor-pointer"><Checkbox data-testid="ob-tos" checked={tos} onCheckedChange={(v) => setTos(v === true)} className="mt-1" /><span className="text-sm text-[color:var(--text-muted)]">I agree to the <Link to="/terms/vendor" target="_blank" className="text-[color:var(--text)] link-underline">Terms of Service</Link></span></label>
          <label className="flex items-start gap-3 cursor-pointer"><Checkbox data-testid="ob-privacy" checked={privacy} onCheckedChange={(v) => setPrivacy(v === true)} className="mt-1" /><span className="text-sm text-[color:var(--text-muted)]">I agree to the <Link to="/privacy" target="_blank" className="text-[color:var(--text)] link-underline">Privacy Policy</Link></span></label>
        </div>
      )}
      <div className="mt-8 flex justify-end">
        <Button data-testid="step-1-continue" onClick={submit} disabled={loading} className="btn-sage rounded-lg h-11 px-6">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
      </div>
    </div>
  );
}

/* ---------- STEP 2 ---------- */
function Step2({ data, onBack, onDone }) {
  const existing = data.business || {};
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    name: existing.name || "",
    categories: existing.categories || [],
    description: existing.description || "",
    contact_phone: existing.contact_phone || "",
    website: existing.website || "",
    address: existing.address || "",
    city: existing.city || "",
    state: existing.state || "",
    postal_code: existing.postal_code || "",
    service_area: existing.service_area || "",
    service_radius_miles: existing.service_radius_miles || "",
    business_hours: existing.business_hours || "",
    price_from: existing.price_from || 0,
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/onboarding/categories").then(({ data }) => setCats(data)); }, []);

  const toggleCat = (key) => {
    setForm(f => ({...f, categories: f.categories.includes(key) ? f.categories.filter(c => c !== key) : [...f.categories, key]}));
  };

  const submit = async () => {
    if (!form.name || !form.categories.length || !form.city) return toast.error("Business name, at least one category and city are required");
    setSaving(true);
    try {
      // Map first category to canonical vendor.category
      const primary = form.categories[0];
      const catMap = { caterer: "catering", food_truck: "catering", bakery: "catering", hotel: "venue", restaurant: "restaurant", venue: "venue", florist: "florist", dj: "dj", rentals: "rentals", av: "av", security: "security" };
      const payload = {
        ...form,
        category: catMap[primary] || "catering",
        tagline: form.description.slice(0, 100),
        price_unit: "per event",
        service_radius_miles: form.service_radius_miles === "" ? null : Number(form.service_radius_miles),
      };
      let vendor;
      if (data.vendor_id) {
        const { data: v } = await api.put(`/vendors/${data.vendor_id}`, payload);
        vendor = v;
      } else {
        const { data: v } = await api.post("/vendors", payload);
        vendor = v;
      }
      toast.success("Business info saved");
      onDone(vendor);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="step-2">
      <h2 className="text-2xl font-semibold mb-2">Business information</h2>
      <p className="text-sm text-[color:var(--text-muted)] mb-6">Tell planners about your business.</p>
      <div className="space-y-4">
        <div><Label className="eyebrow">Business name</Label><Input data-testid="ob-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-2 h-11 bg-white" /></div>
        <div>
          <Label className="eyebrow">Business category (choose one or more)</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="ob-categories">
            {cats.map(c => (
              <button key={c.key} type="button" data-testid={`cat-${c.key}`} onClick={() => toggleCat(c.key)}
                className={`chip ${form.categories.includes(c.key) ? "active" : ""}`}>{c.label}</button>
            ))}
          </div>
        </div>
        <div><Label className="eyebrow">Business description</Label><Textarea data-testid="ob-description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4} className="mt-2 bg-white" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="eyebrow">Phone</Label><Input data-testid="ob-phone" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} className="mt-2 h-11 bg-white" /></div>
          <div><Label className="eyebrow">Website</Label><Input data-testid="ob-website" value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://" className="mt-2 h-11 bg-white" /></div>
        </div>
        <div><Label className="eyebrow">Business address</Label><Input data-testid="ob-address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="mt-2 h-11 bg-white" /></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label className="eyebrow">City</Label><Input data-testid="ob-city" value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="mt-2 h-11 bg-white" /></div>
          <div><Label className="eyebrow">State</Label><Input data-testid="ob-state" value={form.state} onChange={e => setForm({...form, state: e.target.value})} placeholder="VA" maxLength={2} className="mt-2 h-11 bg-white" /></div>
          <div><Label className="eyebrow">ZIP code</Label><Input data-testid="ob-zip" value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value.replace(/[^0-9]/g,'').slice(0,5)})} placeholder="22311" inputMode="numeric" className="mt-2 h-11 bg-white" /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="eyebrow">Service radius (miles)</Label><Input data-testid="ob-radius" type="number" min="0" max="500" value={form.service_radius_miles} onChange={e => setForm({...form, service_radius_miles: e.target.value})} placeholder="35" className="mt-2 h-11 bg-white" /></div>
          <div><Label className="eyebrow">Service area <span className="text-[color:var(--text-muted)] font-normal">(descriptive)</span></Label><Input data-testid="ob-service-area" value={form.service_area} onChange={e => setForm({...form, service_area: e.target.value})} placeholder="e.g. DMV / Tri-state" className="mt-2 h-11 bg-white" /></div>
        </div>
        <div><Label className="eyebrow">Business hours</Label><Input data-testid="ob-hours" value={form.business_hours} onChange={e => setForm({...form, business_hours: e.target.value})} placeholder="Mon–Fri 9am–6pm" className="mt-2 h-11 bg-white" /></div>
        <div><Label className="eyebrow">Starting price</Label><Input data-testid="ob-price" type="number" value={form.price_from} onChange={e => setForm({...form, price_from: +e.target.value})} className="mt-2 h-11 bg-white" /></div>
      </div>
      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={onBack} className="rounded-lg h-11"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <Button data-testid="step-2-continue" onClick={submit} disabled={saving} className="btn-sage rounded-lg h-11 px-6">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
      </div>
    </div>
  );
}

/* ---------- STEP 3 ---------- */
function Step3({ vendorId, onBack, onDone }) {
  const [logo, setLogo] = useState(null);
  const [cover, setCover] = useState(null);
  const [gallery, setGallery] = useState([]);
  const load = () => {
    if (!vendorId) return;
    api.get(`/vendors/${vendorId}/media`).then(({ data }) => {
      setLogo(data.find(m => m.kind === "logo") || null);
      setCover(data.find(m => m.kind === "cover") || null);
      setGallery(data.filter(m => m.kind === "gallery"));
    });
  };
  useEffect(() => { load(); }, [vendorId]);

  const upload = async (file, kind) => {
    if (!file || !vendorId) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File exceeds 5MB");
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result.split(",")[1];
      try {
        await api.post(`/vendors/${vendorId}/media`, { kind, file_name: file.name, file_type: file.type, file_data_b64: b64 });
        toast.success(`${kind} uploaded`);
        load();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  const remove = async (id) => {
    await api.delete(`/vendors/${vendorId}/media/${id}`);
    load();
  };

  // ---------- Website photo import ----------
  const [webUrl, setWebUrl] = useState("");
  const [webOpen, setWebOpen] = useState(false);
  const [webBusy, setWebBusy] = useState(false);
  const [webCandidates, setWebCandidates] = useState([]);
  const [webSelected, setWebSelected] = useState(new Set());

  const scanWebsite = async () => {
    if (!vendorId || !webUrl.trim()) return;
    setWebBusy(true);
    setWebCandidates([]); setWebSelected(new Set());
    try {
      const { data } = await api.post(`/vendors/${vendorId}/gallery/scan-url`, { url: webUrl.trim() });
      const c = data.candidates || [];
      if (c.length === 0) {
        toast.info("We couldn't find usable photos on this website. You can upload photos manually instead.");
      }
      setWebCandidates(c);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) ||
        "We couldn't access that website. Check the URL and try again.";
      toast.error(msg);
    } finally { setWebBusy(false); }
  };

  const toggleSel = (src) => {
    setWebSelected(prev => {
      const n = new Set(prev);
      n.has(src) ? n.delete(src) : n.add(src);
      return n;
    });
  };
  const selectAll = () => setWebSelected(new Set(webCandidates.map(c => c.src)));
  const clearSel  = () => setWebSelected(new Set());

  const importSelected = async () => {
    if (webSelected.size === 0) return;
    setWebBusy(true);
    try {
      const { data } = await api.post(`/vendors/${vendorId}/gallery/import-url`, { urls: [...webSelected] });
      const ok = data.imported_count ?? (data.imported || []).length;
      const bad = data.failed_count ?? (data.failed || []).length;
      if (ok > 0 && bad === 0) toast.success(`Imported ${ok} photo${ok === 1 ? "" : "s"}`);
      else if (ok > 0)         toast.success(`Imported ${ok}, ${bad} could not be imported`);
      else                     toast.error(`Nothing imported — ${bad} failed`);
      setWebOpen(false); setWebCandidates([]); setWebSelected(new Set()); setWebUrl("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setWebBusy(false); }
  };

  const Uploader = ({ kind, label, current }) => (
    <div>
      <Label className="eyebrow">{label}</Label>
      <label
        className="mt-2 block cursor-pointer rounded-xl border-2 border-dashed border-[color:var(--border-strong)] p-6 text-center hover:bg-[color:var(--surface)] transition-colors"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0], kind); }}
      >
        <input data-testid={`upload-${kind}`} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], kind)} />
        {current ? (
          <div className="flex items-center gap-4">
            <img src={current.data_url} alt="" className="w-20 h-20 object-cover rounded-lg" />
            <div className="flex-1 text-left text-sm">
              <div className="font-semibold truncate">{current.file_name}</div>
              <div className="text-[color:var(--text-muted)] text-xs">Click to replace or drag another image</div>
            </div>
            <button type="button" onClick={(e) => { e.preventDefault(); remove(current.media_id); }} className="text-[color:var(--accent)]"><Trash2 className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="text-sm text-[color:var(--text-muted)]"><Upload className="w-5 h-5 mx-auto mb-2" />Drop image here, or click to select</div>
        )}
      </label>
    </div>
  );

  return (
    <div data-testid="step-3">
      <h2 className="text-2xl font-semibold mb-2">Branding</h2>
      <p className="text-sm text-[color:var(--text-muted)] mb-6">Upload your logo, cover photo and gallery images. Drag-and-drop supported.</p>
      <div className="space-y-6">
        <Uploader kind="logo" label="Company logo" current={logo} />
        <Uploader kind="cover" label="Cover photo (wide)" current={cover} />
        <div>
          <Label className="eyebrow">Gallery photos</Label>
          <label
            className="mt-2 block cursor-pointer rounded-xl border-2 border-dashed border-[color:var(--border-strong)] p-6 text-center hover:bg-[color:var(--surface)] transition-colors"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); Array.from(e.dataTransfer.files || []).forEach(f => upload(f, "gallery")); }}
          >
            <input data-testid="upload-gallery" type="file" accept="image/*" multiple className="hidden" onChange={(e) => Array.from(e.target.files || []).forEach(f => upload(f, "gallery"))} />
            <div className="text-sm text-[color:var(--text-muted)]"><Upload className="w-5 h-5 mx-auto mb-2" />Drop multiple images or click to select</div>
          </label>

          {/* Import from website */}
          <div className="mt-3">
            {!webOpen ? (
              <button
                type="button"
                data-testid="open-website-import"
                onClick={() => setWebOpen(true)}
                className="text-xs uppercase tracking-widest text-[color:var(--accent)] underline hover:no-underline"
              >
                Or import from your website URL
              </button>
            ) : (
              <div data-testid="website-import-panel" className="mt-2 p-4 rounded-xl bg-[color:var(--surface)] border border-[color:var(--border-strong)]">
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    data-testid="website-url-input"
                    value={webUrl}
                    onChange={(e) => setWebUrl(e.target.value)}
                    placeholder="https://yourbusiness.com"
                    className="bg-white h-10 flex-1 min-w-[220px]"
                  />
                  <Button
                    type="button"
                    data-testid="website-scan-btn"
                    onClick={scanWebsite}
                    disabled={webBusy || !webUrl.trim()}
                    className="btn-sage rounded-lg h-10 px-4"
                  >
                    {webBusy ? "Scanning…" : "Scan"}
                  </Button>
                  <button
                    type="button"
                    data-testid="website-close-btn"
                    onClick={() => { setWebOpen(false); setWebCandidates([]); setWebSelected(new Set()); setWebUrl(""); }}
                    className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
                  >Cancel</button>
                </div>
                {webCandidates.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">
                        Found {webCandidates.length} · Selected {webSelected.size}
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <button type="button" data-testid="website-select-all" onClick={selectAll} className="underline">Select all</button>
                        <button type="button" data-testid="website-clear-sel" onClick={clearSel} className="underline">Clear</button>
                        <Button
                          type="button"
                          data-testid="website-import-selected"
                          onClick={importSelected}
                          disabled={webBusy || webSelected.size === 0}
                          className="btn-sage rounded-lg h-8 px-3 text-xs"
                        >
                          Import {webSelected.size > 0 ? webSelected.size : ""}
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="website-candidate-grid">
                      {webCandidates.map((c) => {
                        const on = webSelected.has(c.src);
                        return (
                          <button
                            type="button"
                            key={c.src}
                            data-testid="website-candidate"
                            data-selected={on}
                            onClick={() => toggleSel(c.src)}
                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${on ? "border-[color:var(--accent)] ring-2 ring-[color:var(--accent)]/30" : "border-transparent hover:border-[color:var(--border-strong)]"}`}
                            title={c.alt || c.src}
                          >
                            <img src={c.src} alt={c.alt || ""} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                            {on && <span className="absolute top-1 right-1 w-5 h-5 bg-[color:var(--accent)] text-white text-[10px] rounded-full flex items-center justify-center">✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {gallery.length > 0 && (
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="gallery-previews">
              {gallery.map(g => (
                <div key={g.media_id} className="relative aspect-square rounded-lg overflow-hidden group">
                  <img src={g.data_url} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => remove(g.media_id)} className="absolute top-1 right-1 w-7 h-7 bg-black/60 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3.5 h-3.5 mx-auto" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={onBack} className="rounded-lg h-11"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <Button data-testid="step-3-continue" onClick={onDone} className="btn-sage rounded-lg h-11 px-6">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
      </div>
    </div>
  );
}

/* ---------- STEP 4 ---------- */
function Step4({ vendorId, vendorCategory, onBack, onDone }) {
  const isFood = vendorCategory === "catering" || vendorCategory === "restaurant";
  const stepHeading = isFood ? "Menus, packages & services" : "Packages & services";
  const stepIntro = isFood
    ? "Upload existing menu files or add items manually. Uploaded sources are saved — click Extract with AI to structure them, or add items manually below."
    : "Upload an existing brochure/page or add packages & services manually. Uploaded sources are saved — click Extract with AI to structure them, or add items manually below.";
  const [products, setProducts] = useState([]);
  const [imports, setImports] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const emptyManual = { kind: "service", title: "", description: "", price: 0, category: "", availability: "", pricing_type: "flat_rate", booking_mode: "quote" };
  const [manual, setManual] = useState(emptyManual);

  const load = () => {
    if (!vendorId) return;
    api.get(`/vendors/${vendorId}/products?include_drafts=true`).then(({ data }) => setProducts(data));
    api.get(`/vendors/${vendorId}/menu-imports`).then(({ data }) => setImports(data));
  };
  useEffect(() => { load(); }, [vendorId]);

  const extractImport = async (importId) => {
    toast.info("Extraction started — this runs in the background");
    try {
      await api.post(`/menu-imports/${importId}/extract`);
      // Poll for completion up to ~2 minutes
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/menu-imports/${importId}`);
          if (data.status === "processed") {
            toast.success(`Extracted ${data.categories_count || 0} sections`);
            load();
            return;
          }
          if (data.status === "failed") {
            toast.error(data.error || "Extraction failed");
            load();
            return;
          }
        } catch { /* transient — keep polling */ }
        if (tries < 60) setTimeout(poll, 2000);
        else { toast.warning("Still working — refresh in a moment"); load(); }
      };
      setTimeout(poll, 1500);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Extraction failed"); }
  };

  const removeImport = async (importId) => {
    if (!window.confirm("Remove this import source? Published menus and packages are not affected.")) return;
    try {
      await api.delete(`/menu-imports/${importId}`);
      toast.success("Import source removed"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not delete"); }
  };

  const uploadImport = async (file, source) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File exceeds 10MB");
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result.split(",")[1];
      try {
        await api.post(`/vendors/${vendorId}/menu-imports`, { vendor_id: vendorId, source, file_name: file.name, file_type: file.type, file_data_b64: b64 });
        toast.success("Import queued for processing");
        load();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    reader.readAsDataURL(file);
  };

  const addManual = async () => {
    if (!manual.title) return toast.error("Title is required");
    try {
      await api.post(`/vendors/${vendorId}/products`, manual);
      toast.success("Added");
      setManual(emptyManual);
      setShowManual(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approveProduct = async (id) => {
    try {
      await api.post(`/products/${id}/approve`);
      toast.success("Approved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not approve"); }
  };
  const approveAllDrafts = async () => {
    if (!window.confirm("Approve all remaining AI drafts on this listing?")) return;
    try {
      const { data } = await api.post(`/vendors/${vendorId}/products/approve-all`);
      toast.success(`Approved ${data.approved_count} draft${data.approved_count === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not approve"); }
  };
  const clearAllDrafts = async () => {
    const draftCount = products.filter(p => p.ai_draft).length;
    if (!draftCount) { toast.info("No drafts to clear"); return; }
    if (!window.confirm(`Delete ${draftCount} AI draft${draftCount === 1 ? "" : "s"}?\n\nThis permanently removes unapproved items created by AI imports. Approved packages and services will not be affected.`)) return;
    try {
      const { data } = await api.post(`/vendors/${vendorId}/products/drafts/clear`);
      toast.success(`${data.deleted_count} AI draft${data.deleted_count === 1 ? "" : "s"} removed`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not clear drafts"); }
  };
  const removeProduct = async (id) => { await api.delete(`/products/${id}`); load(); };

  const ImportBtn = ({ accept, source, icon: Icon, label, testid }) => (
    <label className="cursor-pointer rounded-lg border border-[color:var(--border-strong)] bg-white p-4 flex flex-col items-center gap-2 hover:bg-[color:var(--surface)] transition-colors text-center">
      <input data-testid={testid} type="file" accept={accept} className="hidden" onChange={(e) => uploadImport(e.target.files?.[0], source)} />
      <Icon className="w-5 h-5" />
      <span className="text-xs font-semibold">{label}</span>
    </label>
  );

  return (
    <div data-testid="step-4">
      <h2 className="text-2xl font-semibold mb-2">{stepHeading}</h2>
      <p className="text-sm text-[color:var(--text-muted)] mb-6">{stepIntro}</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <ImportBtn accept=".csv,.xls,.xlsx" source="spreadsheet" icon={FileSpreadsheet} label="Spreadsheet" testid="import-spreadsheet" />
        <ImportBtn accept=".pdf" source="pdf" icon={FileText} label="PDF" testid="import-pdf" />
        <ImportBtn accept=".doc,.docx" source="word" icon={FileText} label="Word Doc" testid="import-word" />
        <ImportBtn accept="image/*" source="images" icon={FileImage} label="Images" testid="import-images" />
        <button data-testid="import-website" onClick={() => {
          const url = prompt("Enter website URL to import from:");
          if (url) api.post(`/vendors/${vendorId}/menu-imports`, { vendor_id: vendorId, source: "website", url }).then(() => { toast.success("Website queued"); load(); });
        }} className="rounded-lg border border-[color:var(--border-strong)] bg-white p-4 flex flex-col items-center gap-2 hover:bg-[color:var(--surface)] transition-colors">
          <Globe className="w-5 h-5" /><span className="text-xs font-semibold">Import Website</span>
        </button>
        <button data-testid="import-manual" onClick={() => setShowManual(true)} className="rounded-lg border border-[color:var(--border-strong)] bg-white p-4 flex flex-col items-center gap-2 hover:bg-[color:var(--surface)] transition-colors">
          <PencilLine className="w-5 h-5" /><span className="text-xs font-semibold">Enter Manually</span>
        </button>
      </div>

      {showManual && (
        <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] p-5 mb-6 space-y-3" data-testid="manual-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <select data-testid="manual-kind" value={manual.kind} onChange={e => setManual({...manual, kind: e.target.value})} className="h-11 rounded-lg border border-[color:var(--border-strong)] bg-white px-3 text-sm">
              <option value="service">Service</option><option value="product">Product</option><option value="package">Package</option>
            </select>
            <Input data-testid="manual-category" placeholder="Category (optional)" value={manual.category} onChange={e => setManual({...manual, category: e.target.value})} className="bg-white h-11" />
          </div>
          <Input data-testid="manual-title" placeholder="Title" value={manual.title} onChange={e => setManual({...manual, title: e.target.value})} className="bg-white h-11" />
          <Textarea data-testid="manual-description" placeholder="Description" value={manual.description} onChange={e => setManual({...manual, description: e.target.value})} rows={3} className="bg-white" />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1.5" htmlFor="manual-pricing-type">Pricing type</label>
              <select
                id="manual-pricing-type"
                data-testid="manual-pricing-type"
                value={manual.pricing_type}
                onChange={e => {
                  const pt = e.target.value;
                  const forceQuote = pt === "starting_at" || pt === "contact";
                  setManual({ ...manual, pricing_type: pt, booking_mode: forceQuote ? "quote" : manual.booking_mode });
                }}
                className="h-11 w-full rounded-lg border border-[color:var(--border-strong)] bg-white px-3 text-sm"
              >
                <option value="per_person">Per person</option>
                <option value="per_hour">Per hour</option>
                <option value="per_item">Per item</option>
                <option value="flat_rate">Flat rate</option>
                <option value="starting_at">Starting at</option>
                <option value="contact">Contact for pricing</option>
              </select>
            </div>
            <div>
              <label className="eyebrow block mb-1.5" htmlFor="manual-price">
                {{
                  per_person: "Price per person",
                  per_hour: "Hourly rate",
                  per_item: "Price per item",
                  flat_rate: "Package price",
                  starting_at: "Starting price",
                  contact: "Price (optional)",
                }[manual.pricing_type] || "Price"}
              </label>
              <Input
                id="manual-price"
                data-testid="manual-price"
                type="number"
                placeholder={manual.pricing_type === "contact" ? "Leave blank if not applicable" : "Price"}
                value={manual.price}
                onChange={e => setManual({...manual, price: +e.target.value})}
                className="bg-white h-11"
                disabled={false}
              />
            </div>
          </div>
          {(() => {
            const forceQuote = manual.pricing_type === "starting_at" || manual.pricing_type === "contact";
            const instantDisabled = forceQuote;
            return (
              <div className="rounded-lg border border-[color:var(--border)] bg-white p-3 space-y-2" data-testid="manual-booking-method">
                <div className="eyebrow">Booking method</div>
                <label className={`flex items-start gap-2 cursor-pointer ${instantDisabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                  <input
                    type="radio"
                    name="booking_mode"
                    value="instant"
                    checked={manual.booking_mode === "instant"}
                    disabled={instantDisabled}
                    onChange={() => !instantDisabled && setManual({ ...manual, booking_mode: "instant" })}
                    data-testid="manual-booking-mode-instant"
                    className="mt-0.5 shrink-0"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">Instant Booking</div>
                    <div className="text-[12px] text-[color:var(--text-muted)] leading-snug">
                      Customers can select this offering, see the calculated total, and pay the required deposit without waiting for you to approve a quote.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="booking_mode"
                    value="quote"
                    checked={manual.booking_mode === "quote"}
                    onChange={() => setManual({ ...manual, booking_mode: "quote" })}
                    data-testid="manual-booking-mode-quote"
                    className="mt-0.5 shrink-0"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold">Quote Required</div>
                    <div className="text-[12px] text-[color:var(--text-muted)] leading-snug">
                      Customers send a request first. You review the details and send the final quote before payment is available.
                    </div>
                  </div>
                </label>
                {instantDisabled && (
                  <div className="text-[11px] text-[color:var(--text-muted)] italic pt-1" data-testid="manual-booking-mode-forced">
                    Instant Booking isn't available for "{manual.pricing_type === "starting_at" ? "Starting at" : "Contact for pricing"}" — the price isn't final yet.
                  </div>
                )}
              </div>
            );
          })()}
          <div className="grid sm:grid-cols-1 gap-3">
            <Input data-testid="manual-availability" placeholder="Availability" value={manual.availability} onChange={e => setManual({...manual, availability: e.target.value})} className="bg-white h-11" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowManual(false)} className="rounded-lg">Cancel</Button>
            <Button data-testid="manual-save" onClick={addManual} className="btn-sage rounded-lg">Add item</Button>
          </div>
        </div>
      )}

      {imports.length > 0 && (
        <div className="mb-6">
          <div className="eyebrow mb-3">Import sources</div>
          <div className="space-y-2" data-testid="imports-list">
            {imports.map(i => {
              const canExtract = i.status !== "processed" && i.status !== "processing";
              const label = i.status === "pending_processing" ? "ready to extract" : i.status;
              return (
                <div key={i.import_id} className="text-sm p-3 border border-[color:var(--border)] rounded-lg bg-white">
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate">{i.file_name || i.url} <span className="text-[color:var(--text-muted)]">· {i.source}</span></span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</span>
                      {canExtract && (
                        <Button data-testid={`onb-extract-${i.import_id}`} onClick={() => extractImport(i.import_id)}
                          variant="outline" size="sm" className="h-8 text-xs rounded-lg">Extract with AI</Button>
                      )}
                      {i.status !== "processing" && (
                        <button data-testid={`onb-delete-${i.import_id}`} type="button" onClick={() => removeImport(i.import_id)}
                          className="text-[color:var(--accent)] hover:opacity-80"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                  {i.error && <div className="text-xs text-[color:var(--accent)] mt-1">{i.error}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {products.length > 0 && (() => {
        const draftCount = products.filter(p => p.ai_draft).length;
        return (
          <div className="mb-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="eyebrow">Items ({products.length}{draftCount > 0 ? ` · ${draftCount} draft${draftCount === 1 ? "" : "s"}` : ""})</div>
              {draftCount > 0 && (
                <div className="flex items-center gap-2">
                  <Button data-testid="approve-all-drafts" onClick={approveAllDrafts}
                    variant="outline" size="sm" className="h-8 text-xs rounded-lg">Approve all drafts</Button>
                  <Button data-testid="clear-all-drafts" onClick={clearAllDrafts}
                    variant="outline" size="sm" className="h-8 text-xs rounded-lg border-[color:var(--accent)] text-[color:var(--accent)] hover:bg-[color:var(--accent)]/5">Clear all AI drafts</Button>
                </div>
              )}
            </div>
            <div className="space-y-2" data-testid="products-list">
              {products.map(p => (
                <div key={p.product_id} className={`p-4 border rounded-lg flex items-center justify-between gap-3 ${p.ai_draft ? "border-[#FDE68A] bg-[#FFFBEB]" : "border-[color:var(--border)] bg-white"}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                      <span>{p.title}</span>
                      <span className="text-xs uppercase tracking-widest text-[color:var(--text-muted)]">{p.kind}</span>
                      {p.ai_draft && (
                        <span data-testid={`onb-draft-badge-${p.product_id}`} className="text-[10px] uppercase tracking-widest bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 rounded-full">Draft</span>
                      )}
                    </div>
                    <div className="text-xs text-[color:var(--text-muted)] truncate">{p.description}</div>
                  </div>
                  <div className="text-sm font-semibold shrink-0">{p.price > 0 ? `$${p.price}` : (p.ai_starting_price ? `From $${p.ai_starting_price}` : "—")}</div>
                  <div className="flex items-center gap-2 shrink-0">
                    {p.ai_draft && (
                      <Button data-testid={`approve-product-${p.product_id}`} onClick={() => approveProduct(p.product_id)}
                        variant="outline" size="sm" className="h-8 text-xs rounded-lg">Approve</Button>
                    )}
                    <button data-testid={`delete-product-${p.product_id}`} onClick={() => removeProduct(p.product_id)} className="text-[color:var(--accent)]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={onBack} className="rounded-lg h-11"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <Button data-testid="step-4-continue" onClick={onDone} className="btn-sage rounded-lg h-11 px-6">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
      </div>
    </div>
  );
}

/* ---------- STEP 5 ---------- */
function Step5({ vendorId, onBack, onDone }) {
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    api.get("/vendors/mine/list").then(({ data }) => setVendors(vendorId ? data.filter(v => v.vendor_id === vendorId) : data));
  }, [vendorId]);
  return (
    <div data-testid="step-5">
      <div className="rounded-xl bg-[color:var(--surface)] p-5 mb-6 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[color:var(--accent)] shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Verification is optional but strongly encouraged</div>
          <div className="text-sm text-[color:var(--text-muted)] mt-1">Customers are more likely to book verified businesses. Verification helps build trust and increases your visibility on NContro.</div>
        </div>
      </div>
      <VendorVerification vendors={vendors} />
      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={onBack} className="rounded-lg h-11"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <Button data-testid="step-5-continue" onClick={onDone} className="btn-sage rounded-lg h-11 px-6">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
      </div>
    </div>
  );
}

/* ---------- STEP 6 ---------- */
function Step6({ onDashboard, onMore }) {
  return (
    <div data-testid="step-6" className="text-center py-8">
      <div className="w-16 h-16 mx-auto rounded-full bg-[color:var(--verified-soft)] flex items-center justify-center text-[color:var(--verified)] mb-6"><PartyPopper className="w-8 h-8" /></div>
      <h2 className="text-3xl font-semibold mb-2">Congratulations!</h2>
      <p className="text-[color:var(--text-muted)] max-w-md mx-auto mb-8">Your profile has been created. Your listing is now pending review — we'll email you once it's live.</p>
      <div className="flex flex-wrap gap-3 justify-center">
        <Button data-testid="go-to-dashboard" onClick={onDashboard} className="btn-sage rounded-lg h-11 px-6">Go to dashboard</Button>
        <Button data-testid="complete-more" variant="outline" onClick={onMore} className="rounded-lg h-11 px-6">Complete more information</Button>
      </div>
    </div>
  );
}
